import { motion } from "framer-motion";
import PeekInsideBook from "../components/PeekInsideBook";
import FourVerbs from "../components/FourVerbs";

const BOOKS = [
  {
    n: "01",
    title: "Love Well",
    theme: "Parental absence & estrangement",
    body: "The first book in the series — a picture book for children ages 4–8 navigating an absent, inconsistent, or complicated parent, and for the grown-ups holding steady alongside them.",
    tool: "The sentence you can hold — 'That isn't your fault, and it isn't your fix.'",
    one: "Some people love well. Others love differently.",
    state: "In production",
    year: "2026",
  },
  {
    n: "02",
    title: "Being Thankful",
    theme: "Gratitude as a daily practice",
    body: "A picture book for families building a small daily ritual around what a child notices and holds on to.",
    tool: "The thank-you rock — a small object to carry, hold, and share.",
    one: "When you notice good things, more good things show up.",
    state: "Drafting",
    year: "2027",
  },
  {
    n: "03",
    title: "Listening Inside",
    theme: "Discernment & the \u201Cuh-oh feeling\u201D",
    body: "A picture book for children learning to notice the quiet alarms inside — and who to bring them to.",
    tool: "Naming the 'uh-oh feeling,' and knowing the trusted adults on the list.",
    one: "The uh-oh feeling is a real feeling. Tell someone you trust.",
    state: "Outlined",
    year: "2028",
  },
  {
    n: "04",
    title: "Being Small and Strong",
    theme: "Inner strength & self-advocacy",
    body: "A picture book for quiet children practicing the honest voice that helps them ask for what they need.",
    tool: "The small strong voice inside — practicing 'no' and 'yes' with intention.",
    one: "Small on the outside. Mighty on the inside.",
    state: "Concept",
    year: "2029",
  },
];

export default function Books() {
  return (
    <>
      <section className="pt-32 md:pt-40 pb-16">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="mono text-[11px] tracking-[0.28em] uppercase text-[#4A7C7B] mb-4">The series</div>
          <h1 className="serif text-[clamp(3rem,8vw,7.5rem)] leading-[0.98] tracking-tight text-espresso">
            Four books. <span className="italic text-[#4A7C7B]">One steady voice.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-lg leading-relaxed text-charcoal/90">
            A picture-book series written for children ages 4–8 and the grown-ups reading alongside them — a small, deliberate library for parents holding steady, teachers reading aloud, and counselors opening a hard conversation with care. Four books, one growing world, one voice throughout.
          </p>
          <p className="mt-10 italic-serif text-lg md:text-xl text-charcoal/90 max-w-3xl border-l-2 border-[#4A7C7B] pl-5">
            A gentle library that grows with a child — from noticing what hurts, to noticing what heals, to trusting one's own voice.
          </p>
        </div>
      </section>

      <section data-testid="books-grid" className="py-8 md:py-16">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-8 md:gap-12">
          {BOOKS.map((b, idx) => (
            <motion.article
              key={b.n}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.25 }}
              transition={{ duration: 0.7, delay: idx * 0.05 }}
              data-testid={`book-card-${b.n}`}
              className="relative torn-frame bg-[#FFFDF9] p-8 md:p-12 min-h-[420px] flex flex-col justify-between"
            >
              <div>
                <div className="flex items-baseline justify-between gap-3">
                  <div className="chapter-number text-[5rem] md:text-[7rem] leading-none">{b.n}</div>
                  <div className="text-right">
                    <div className="mono text-[10px] tracking-[0.28em] uppercase text-[#4A7C7B]">{b.state}</div>
                    <div className="mono text-[10px] tracking-[0.28em] uppercase text-charcoal/70 mt-1">{b.year}</div>
                  </div>
                </div>
                <h2 className="serif text-3xl md:text-4xl leading-tight text-espresso mt-4">{b.title}</h2>
                <div className="mono text-[10px] tracking-[0.28em] uppercase text-charcoal/70 mt-3">Theme · {b.theme}</div>
                <p className="text-sm text-charcoal/85 leading-relaxed mt-4 max-w-md">{b.body}</p>
                <p className="text-xs text-charcoal/70 leading-relaxed mt-3 max-w-md">
                  <span className="mono tracking-[0.24em] uppercase text-[#4A7C7B]">Tool · </span>
                  <span>{b.tool}</span>
                </p>
              </div>
              <p className="italic-serif text-lg md:text-xl text-charcoal mt-10 border-l-2 border-[#4A7C7B] pl-5">
                "{b.one}"
              </p>
            </motion.article>
          ))}
        </div>
      </section>

      <FourVerbs variant="compact" />

      <PeekInsideBook />
    </>
  );
}
